import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { api } from '../lib/api';
import { formatPrice } from '../lib/currency';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';
import { Globe, Wifi, Zap, Signal, Calendar, Check, ArrowLeft, Search, Smartphone,
  ExternalLink, MapPin, Repeat, ShieldCheck } from 'lucide-react';
import { Seo } from '../components/Seo';

const SORTS = [['recommended', 'Recommended'], ['cheapest', 'Cheapest'], ['best_value', 'Best value'], ['most_data', 'Most data'], ['longest', 'Longest validity']];

const BADGE_STYLE = {
  'Cheapest': 'bg-emerald-600 text-white border-emerald-600',
  'Best Value': 'bg-accent text-accent-foreground border-accent',
  'Best Overall': 'bg-blue-600 text-white border-blue-600',
};

const buyPlan = (p) => {
  api.esimTrack('buy_clicked', { provider: p.provider, product_id: p.product_key, destination: p.country_code, data_amount: p.data_amount, validity: p.validity_days, price: p.price_inr, currency: 'INR' });
  window.open(api.esimBuyUrl(p.product_key), '_blank', 'noopener');
};

const Price = ({ p, currency, size = 'lg' }) => (
  <div className="text-right shrink-0">
    {p.original_price_inr && p.original_price_inr > p.price_inr && (
      <p className="text-xs text-muted-foreground line-through" data-testid={`esim-orig-${p.product_key}`}>{formatPrice(p.original_price_inr, currency)}</p>
    )}
    <p className={`${size === 'lg' ? 'text-2xl' : 'text-3xl'} font-semibold tabular text-accent`} data-testid={`esim-price-${p.product_key}`}>{formatPrice(p.price_inr, currency)}</p>
    <p className="text-[10px] text-muted-foreground">current price</p>
  </div>
);

const PlanCard = ({ p, currency, onView }) => (
  <Card className="p-4 rounded-2xl border-border card-elevate" data-testid={`esim-plan-${p.product_key}`}>
    {p.badges?.length > 0 && (
      <div className="flex flex-wrap gap-1.5 mb-2">
        {p.badges.map((b) => <Badge key={b} className={`rounded-full text-[10px] font-semibold ${BADGE_STYLE[b] || ''}`} data-testid={`esim-badge-${b.toLowerCase().replace(/ /g, '-')}`}>{b}</Badge>)}
      </div>
    )}
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {p.provider_logo ? <img src={p.provider_logo} alt="" className="h-4" onError={(e) => { e.target.style.display = 'none'; }} /> : null}
          <span className="font-semibold text-sm">{p.provider_name}</span>
          {p.is_regional && <Badge variant="outline" className="rounded-full text-[9px]"><MapPin className="h-2.5 w-2.5 mr-0.5" />{p.coverage?.length} countries</Badge>}
          {p.value_score > 0 && <span className="text-[10px] text-muted-foreground">value {p.value_score}/100</span>}
        </div>
        <p className="text-sm font-medium mt-1 truncate">{p.name}</p>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-muted-foreground">
          <span className="inline-flex items-center gap-1 text-foreground font-medium"><Wifi className="h-3.5 w-3.5 text-accent" />{p.data_amount}</span>
          <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{p.validity_days} day{p.validity_days === 1 ? '' : 's'}</span>
          <span className="inline-flex items-center gap-1"><Signal className="h-3.5 w-3.5" />{p.network}</span>
          {p.is_5g && <span className="inline-flex items-center gap-1"><Zap className="h-3.5 w-3.5" />5G</span>}
          {p.roaming_supported && <span className="inline-flex items-center gap-1"><Repeat className="h-3.5 w-3.5" />Roaming</span>}
        </div>
      </div>
      <Price p={p} currency={currency} />
    </div>
    <div className="flex gap-2 mt-3">
      <Button variant="outline" className="rounded-xl flex-1" onClick={() => onView(p)} data-testid={`esim-details-${p.product_key}`}>Details</Button>
      <Button onClick={() => buyPlan(p)} className="rounded-xl flex-1 bg-accent text-accent-foreground hover:bg-accent/90 font-semibold" data-testid={`esim-cta-${p.product_key}`}>Buy eSIM <ExternalLink className="h-4 w-4 ml-1.5" /></Button>
    </div>
  </Card>
);

function EsimLanding() {
  const [meta, setMeta] = useState(null);
  const [regions, setRegions] = useState([]);
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  const { currency } = useApp();
  useEffect(() => {
    document.title = 'Travel eSIM — Compare data plans | FlightDeals.in';
    api.esimMeta().then(({ data }) => setMeta(data)).catch(() => setMeta({ countries: [], popular: [], provider: {} }));
    api.esimRegions().then(({ data }) => setRegions(data.regions || [])).catch(() => setRegions([]));
  }, []);
  const all = meta?.countries || [];
  const filtered = q ? all.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()) || c.code.toLowerCase().includes(q.toLowerCase())) : (meta?.popular || []);
  return (
    <Layout>
      <Seo path="/esim" title="Travel eSIM — Compare Data Plans for 190+ Countries"
        description="Compare prepaid travel eSIM data plans across providers by destination. No physical SIM, no roaming bills — scan a QR and get online the moment you land."
        jsonLd={{ '@context': 'https://schema.org', '@type': 'ItemList', name: 'Travel eSIM plans', itemListElement: (meta?.popular || []).slice(0, 12).map((c, i) => ({ '@type': 'ListItem', position: i + 1, name: `${c.name} eSIM`, url: `https://flightdeals.in/esim/${c.slug}` })) }} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="rounded-3xl border border-border bg-gradient-to-br from-accent/10 to-transparent p-7 mb-6">
          <Badge className="rounded-full bg-accent/15 text-accent mb-2"><Globe className="h-3.5 w-3.5 mr-1" />Compare eSIMs</Badge>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold">Stay connected abroad — instantly</h1>
          <p className="text-muted-foreground mt-1 max-w-2xl">Compare live travel eSIM data plans across providers. No physical SIM, no roaming bills — scan a QR and you're online the moment you land.</p>
          <div className="relative mt-4 max-w-md">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search 175+ destinations…" className="pl-9 rounded-xl h-11" data-testid="esim-search" />
          </div>
          {meta && (
            <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5" data-testid="esim-catalogue-note">
              <ShieldCheck className="h-3.5 w-3.5 text-accent" /> {meta.catalogue_size} live plans across {meta.countries?.length || 0} destinations · prices update automatically
            </p>
          )}
        </div>
        <h2 className="font-semibold text-lg mb-3">{q ? `Results for “${q}”` : 'Popular destinations'}</h2>        {!meta ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}</div>
        ) : filtered.length === 0 ? (
          <Card className="p-12 rounded-2xl text-center" data-testid="esim-empty"><Globe className="h-10 w-10 mx-auto text-muted-foreground mb-2" /><p className="font-semibold">No destinations found</p><p className="text-sm text-muted-foreground">Try another country name.</p></Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="esim-country-grid">
            {filtered.map((c) => (
              <Card key={c.slug} className="rounded-2xl overflow-hidden group cursor-pointer card-elevate" onClick={() => navigate(`/esim/${c.slug}`)} data-testid={`esim-country-${c.slug}`}>
                <div className="aspect-[16/9] bg-gradient-to-br from-accent/20 to-secondary overflow-hidden">{c.image && <img src={c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" style={{ transitionProperty: 'transform' }} onError={(e) => { e.target.style.display = 'none'; }} />}</div>
                <div className="p-4 flex items-center justify-between">
                  <div><p className="font-semibold">{c.name}</p><p className="text-xs text-muted-foreground">{c.plan_count} plans{c.region ? ` · ${c.region}` : ''}</p></div>
                  <div className="text-right"><p className="text-[10px] text-muted-foreground">from</p><p className="font-semibold text-accent tabular">{formatPrice(c.from_price_inr || 0, currency)}</p></div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {!q && regions.length > 0 && (
          <div className="mt-10" data-testid="esim-regions">
            <h2 className="font-semibold text-lg mb-1">Browse by region</h2>
            <p className="text-sm text-muted-foreground mb-3">Multi-country plans that keep you online across a whole region.</p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {regions.map((r) => (
                <Card key={r.slug} className="rounded-2xl overflow-hidden group cursor-pointer card-elevate" onClick={() => navigate(`/esim/region/${r.slug}`)} data-testid={`esim-region-${r.slug}`}>
                  <div className="aspect-[16/9] bg-gradient-to-br from-accent/25 to-secondary overflow-hidden relative">
                    {r.image && <img src={r.image} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 duration-300" style={{ transitionProperty: 'transform' }} onError={(e) => { e.target.style.display = 'none'; }} />}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
                    <div className="absolute bottom-2 left-3 text-white"><p className="font-semibold text-lg leading-tight">{r.name}</p><p className="text-[11px] opacity-90">up to {r.max_countries} countries</p></div>
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">{r.plan_count} regional plans</p>
                    <div className="text-right"><p className="text-[10px] text-muted-foreground">from</p><p className="font-semibold text-accent tabular">{formatPrice(r.from_price_inr || 0, currency)}</p></div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function CountryPlans({ slug }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [sort, setSort] = useState('recommended');
  const navigate = useNavigate();
  const { currency } = useApp();
  useEffect(() => {
    setData(null); setErr(false);
    api.esimCountry(slug, sort).then(({ data }) => { setData(data); document.title = `${data.country.name} eSIM plans | FlightDeals.in`; api.esimTrack('country_selected', { destination: slug }); }).catch(() => setErr(true));
  }, [slug, sort]);
  const view = (p) => navigate(`/esim/${slug}/${encodeURIComponent(p.product_key)}`);
  return (
    <Layout>
      <Seo path={`/esim/${slug}`} title={data ? `${data.country.name} eSIM Plans — Compare & Buy` : 'eSIM Plans'}
        description={data ? `Compare ${data.country.name} travel eSIM data plans by price, data and validity. Instant QR delivery, no roaming bills.` : 'Compare travel eSIM data plans by destination.'}
        jsonLd={data ? {
          '@context': 'https://schema.org', '@type': 'ItemList',
          name: `${data.country.name} travel eSIM plans`,
          itemListElement: data.plans.slice(0, 30).map((p, i) => ({
            '@type': 'ListItem', position: i + 1,
            item: {
              '@type': 'Product',
              name: `${data.country.name} eSIM — ${p.data_amount}, ${p.validity_days} day${p.validity_days === 1 ? '' : 's'}`,
              category: 'Travel eSIM', brand: { '@type': 'Brand', name: p.provider },
              offers: {
                '@type': 'Offer', price: p.price_inr, priceCurrency: 'INR',
                availability: 'https://schema.org/InStock',
                url: `https://flightdeals.in/esim/${slug}/${encodeURIComponent(p.product_key)}`,
              },
            },
          })),
        } : null} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/esim')} className="text-sm text-muted-foreground hover:text-accent flex items-center gap-1 mb-3" data-testid="esim-back"><ArrowLeft className="h-4 w-4" /> All destinations</button>
        {err ? (
          <Card className="p-12 rounded-2xl text-center" data-testid="esim-country-error"><Globe className="h-10 w-10 mx-auto text-muted-foreground mb-2" /><p className="font-semibold">No eSIM plans for this destination yet</p><p className="text-sm text-muted-foreground mt-1">Plans appear here as soon as a provider offers coverage.</p><Button variant="outline" className="rounded-xl mt-3" onClick={() => navigate('/esim')}>Browse destinations</Button></Card>
        ) : !data ? (
          <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-1">
              {data.country.image && <img src={data.country.image} alt="" className="h-12 w-16 rounded-lg object-cover" onError={(e) => { e.target.style.display = 'none'; }} />}
              <div><h1 className="font-display text-2xl sm:text-3xl font-semibold" data-testid="esim-country-title">{data.country.name} eSIM plans</h1><p className="text-sm text-muted-foreground">Comparing {data.plans.length} plans from {data.providers.join(', ')}</p></div>
            </div>
            <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar my-4" data-testid="esim-sort">
              {SORTS.map(([v, l]) => (
                <button key={v} onClick={() => setSort(v)} className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border transition-colors ${sort === v ? 'bg-accent/12 text-accent border-accent/30' : 'bg-card text-muted-foreground border-border hover:bg-secondary'}`} data-testid={`esim-sort-${v}`}>{l}</button>
              ))}
            </div>
            <div className="space-y-3" data-testid="esim-plans">
              {data.plans.map((p) => <PlanCard key={p.product_key} p={p} currency={currency} onView={view} />)}
            </div>
            <p className="text-[11px] text-muted-foreground mt-4">{data.disclosure}</p>
          </>
        )}
      </div>
    </Layout>
  );
}

function PlanDetail({ pkey }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const navigate = useNavigate();
  const { currency } = useApp();
  useEffect(() => { setData(null); setErr(false); api.esimPlan(pkey).then(({ data }) => { setData(data); document.title = `${data.plan.name} ${data.plan.data_amount} eSIM | FlightDeals.in`; api.esimTrack('product_view', { product_id: pkey, provider: data.plan.provider }); }).catch(() => setErr(true)); }, [pkey]);
  if (err) return <Layout><div className="max-w-2xl mx-auto py-24 text-center"><Globe className="h-10 w-10 mx-auto text-muted-foreground mb-2" /><p className="font-semibold">Plan not found</p><Button variant="outline" className="rounded-xl mt-3" onClick={() => navigate('/esim')}>Browse eSIMs</Button></div></Layout>;
  if (!data) return <Layout><div className="max-w-3xl mx-auto px-4 py-8 space-y-3"><Skeleton className="h-8 w-64 rounded" /><Skeleton className="h-48 rounded-2xl" /></div></Layout>;
  const p = data.plan;
  const specs = [['Data', p.data_amount], ['Validity', `${p.validity_days} day${p.validity_days === 1 ? '' : 's'}`], ['Network', p.network], ['5G', p.is_5g ? 'Yes' : 'No'], ['Hotspot', p.hotspot_supported ? 'Allowed' : 'No'], ['Roaming', p.roaming_supported ? 'Multi-country' : 'Single country'], ['Coverage', (p.coverage || []).join(', ')], ['Activation', p.activation_type]];
  const backSlug = (p.country_slugs || [])[0] || 'esim';
  return (
    <Layout>
      <Seo path={`/esim/${backSlug}/${p.product_key}`} title={`${p.name} ${p.data_amount} eSIM — ${p.provider_name}`}
        description={`${p.name} travel eSIM: ${p.data_amount}, ${p.validity_days} days on ${p.network}. Instant QR delivery.`}
        jsonLd={{ '@context': 'https://schema.org', '@type': 'Product', name: `${p.name} ${p.data_amount} eSIM`, brand: p.provider_name, category: 'Travel eSIM', offers: { '@type': 'Offer', priceCurrency: 'INR', price: Math.round(p.price_inr || 0), availability: 'https://schema.org/InStock' } }} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate(`/esim/${backSlug}`)} className="text-sm text-muted-foreground hover:text-accent flex items-center gap-1 mb-3" data-testid="esim-plan-back"><ArrowLeft className="h-4 w-4" /> Back to plans</button>
        <Card className="rounded-2xl overflow-hidden" data-testid="esim-plan-detail">
          <div className="p-5 border-b border-border flex items-start justify-between gap-4">
            <div>
              {p.badges?.length > 0 && <div className="flex flex-wrap gap-1.5 mb-1.5">{p.badges.map((b) => <Badge key={b} className={`rounded-full text-[10px] font-semibold ${BADGE_STYLE[b] || ''}`}>{b}</Badge>)}</div>}
              <h1 className="font-display text-2xl font-semibold">{p.name} · {p.data_amount}</h1>
              <p className="text-sm text-muted-foreground mt-1">by {p.provider_name}</p>
            </div>
            <Price p={p} currency={currency} size="xl" />
          </div>
          <div className="p-5 grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {specs.map(([k, v]) => <div key={k} className="flex items-start justify-between gap-3 border-b border-border/60 py-1.5"><span className="text-sm text-muted-foreground shrink-0">{k}</span><span className="text-sm font-medium text-right">{v || '—'}</span></div>)}
          </div>
          <div className="p-5 pt-0">
            <div className="rounded-xl bg-secondary/50 p-3 text-xs text-muted-foreground mb-3 flex items-start gap-2"><Smartphone className="h-4 w-4 mt-0.5 shrink-0" />Requires an eSIM-capable device (iPhone XS+/modern Android). Install by scanning the QR you receive after purchase.</div>
            <Button onClick={() => buyPlan(p)} className="w-full h-12 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold" data-testid="esim-plan-cta">Buy this eSIM <ExternalLink className="h-4 w-4 ml-1.5" /></Button>
            <p className="text-[11px] text-muted-foreground text-center mt-2">Opens {p.provider_name}</p>
          </div>
        </Card>
        {data.related?.length > 0 && (
          <div className="mt-6"><h2 className="font-semibold text-sm mb-2 flex items-center gap-1"><Check className="h-4 w-4 text-accent" />Other plans for this destination</h2><div className="space-y-3">{data.related.map((r) => <PlanCard key={r.product_key} p={r} currency={currency} onView={(x) => navigate(`/esim/${backSlug}/${encodeURIComponent(x.product_key)}`)} />)}</div></div>
        )}
      </div>
    </Layout>
  );
}

function RegionPlans({ slug }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [sort, setSort] = useState('recommended');
  const navigate = useNavigate();
  const { currency } = useApp();
  useEffect(() => {
    setData(null); setErr(false);
    api.esimRegion(slug, sort).then(({ data }) => { setData(data); document.title = `${data.region.name} regional eSIM | FlightDeals.in`; api.esimTrack('country_selected', { destination: `region:${slug}` }); }).catch(() => setErr(true));
  }, [slug, sort]);
  const view = (p) => navigate(`/esim/${(p.country_slugs && p.country_slugs[0]) || 'global'}/${encodeURIComponent(p.product_key)}`);
  return (
    <Layout>
      <Seo path={`/esim/region/${slug}`} title={data ? `${data.region.name} Regional eSIM — Compare Multi-Country Plans` : 'Regional eSIM Plans'}
        description={data ? `Compare ${data.region.name} multi-country travel eSIM plans that keep you online across the region. Instant QR delivery.` : 'Compare regional multi-country travel eSIM plans.'} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/esim')} className="text-sm text-muted-foreground hover:text-accent flex items-center gap-1 mb-3" data-testid="esim-back"><ArrowLeft className="h-4 w-4" /> All destinations</button>
        {err ? (
          <Card className="p-12 rounded-2xl text-center" data-testid="esim-region-error"><Globe className="h-10 w-10 mx-auto text-muted-foreground mb-2" /><p className="font-semibold">No regional plans here yet</p><Button variant="outline" className="rounded-xl mt-3" onClick={() => navigate('/esim')}>Browse destinations</Button></Card>
        ) : !data ? (
          <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-1">
              {data.region.image && <img src={data.region.image} alt="" className="h-12 w-16 rounded-lg object-cover" onError={(e) => { e.target.style.display = 'none'; }} />}
              <div><h1 className="font-display text-2xl sm:text-3xl font-semibold" data-testid="esim-region-title">{data.region.name} regional eSIM</h1><p className="text-sm text-muted-foreground">Multi-country plans · comparing {data.plans.length} from {data.providers.join(', ')}</p></div>
            </div>
            <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar my-4" data-testid="esim-sort">
              {SORTS.map(([v, l]) => (
                <button key={v} onClick={() => setSort(v)} className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border transition-colors ${sort === v ? 'bg-accent/12 text-accent border-accent/30' : 'bg-card text-muted-foreground border-border hover:bg-secondary'}`} data-testid={`esim-sort-${v}`}>{l}</button>
              ))}
            </div>
            <div className="space-y-3" data-testid="esim-plans">
              {data.plans.map((p) => <PlanCard key={p.product_key} p={p} currency={currency} onView={view} />)}
            </div>
            <p className="text-[11px] text-muted-foreground mt-4">{data.disclosure}</p>
          </>
        )}
      </div>
    </Layout>
  );
}

export default function Esim() {
  const { country, plan } = useParams();
  if (country === 'region' && plan) return <RegionPlans slug={plan} />;
  if (plan) return <PlanDetail pkey={decodeURIComponent(plan)} />;
  if (country) return <CountryPlans slug={country} />;
  return <EsimLanding />;
}
